import {Tools} from "./tools.js";
import {share, Subject, take, takeUntil, tap} from "rxjs";
import {elementFactory} from "./element-factory.js";

/**
 * Represents a collection of elements associated with a form, subform, or enclosing element.
 * Manages elements, child collections, events, and form-related operations.
 */
export class ElementCollection {
    parentForm;
    elements = {};
    configuration = {};
    changeEvent;
    changeStream;

    windowResizeEvent;

    subscriptionCleanup = new Subject();
    isDestroyed = false;

    parentCollection;
    childrenCollections;

    changeEventSubscribberCount = 0;
    /**
     * Retrieves the root collection of the current collection hierarchy.
     * If the collection has a parent, the method recursively traverses through the parent collections
     * until the root collection is found. If the collection has no parent, it returns itself as the root.
     *
     * @return {Object} The root collection of the hierarchy.
     */
    get rootCollection() {
        if (this.parentCollection) {
            return this.parentCollection.rootCollection;
        }

        return this;
    }

    /**
     * Retrieves the mobile bounds of the parent form.
     *
     * @return {Object} An object representing the mobile bounds of the parent form.
     */
    get mobileBounds() {
        return this.parentForm.mobileBounds;
    }

    /**
     * Determines if all elements and child collections are valid.
     *
     * @return {boolean} Returns true if all elements and child collections are valid; otherwise, false.
     */
    get isValid() {
        const elements = this.elements == null ? [] : Object.values(this.elements);
        if (elements.length === 0 && (this.childrenCollections?.length ?? 0) === 0) {
            return true;
        }

        const invalidElements = [
            ...elements.filter(element => !element.isValid),
            ...this.childrenCollections?.filter(collection => !collection.isValid) ?? []
        ];

        return invalidElements.length === 0;
    }

    /**
     * Determines if the current environment or context is mobile.
     *
     * @return {boolean} Returns true if the current context is considered mobile, otherwise false.
     */
    get isMobile() {
        return this.rootCollection?.parentForm?.isMobile ?? false;
    }

    /**
     * Constructs an instance of the class, linking it to its parent object and setting up required streams and events.
     *
     * @param {object} enclosingParent - The parent object to which this instance belongs. Must be a valid object.
     * @param {object} [parentCollection] - An optional parent collection that can include the current instance as a child.
     * @throws {Error} Throws an error if the `enclosingParent` parameter is not provided or is not a valid object.
     * @return {void} This constructor does not return a value.
     */
    constructor(enclosingParent, parentCollection) {
        if (!enclosingParent || typeof enclosingParent !== "object") {
            throw new Error("Unable to create collection: Invalid parent object provided or missing configuration.");
        }

        parentCollection?.addChild(this);

        this.parentForm = enclosingParent;
        this.configuration = enclosingParent.configuration;
        this.changeEvent = new Subject();
        this.changeStream = this.changeEvent.pipe(share());

        this.windowResizeEvent = new Subject();
    }

    /**
     * Add a child Collection
     * @param childCollection - child collection to add
     */
    addChild(childCollection) {
        childCollection.parentCollection = this;
        this.childrenCollections = Array.isArray(this.childrenCollections)
            ? [...this.childrenCollections, childCollection]
            : [childCollection];
    }

    /**
     * Get the data value following the field patch
     * @param fieldPath Field path denoted by '.'
     * @returns {*} The value or object at the field path
     */
    getDataValue(fieldPath) {
        const path = fieldPath.split(".");

        let value = this.rootCollection.parentForm.formData;
        if (value == null) {
            return null;
        }

        for (const pathElement of path) {
            value = value[pathElement];
            if (value == null) {
                break;
            }
        }

        return value;
    }

    /**
     * Send a blip to the RxJS Subject / observable to fire to the subscribers.
     * @param args - event args sent from the emitter.
     */
    emitChangeNotification(args) {
        if (this.isDestroyed) {
            throw new Error("Unable to emit change notifications: Element Collection is destroyed.");
        }

        args.collection = this;
        this.rootCollection.changeEvent.next(args);
    }

    /**
     * Emits a resize event for the collection with the provided arguments.
     * Throws an error if the collection is destroyed.
     *
     * @param {Object} args - The arguments to pass with the resize event.
     * @param {Object} args.collection - The collection that emits the resize event.
     * @return {void} Does not return anything.
     */
    emitResizeEvent(args) {
        if (this.isDestroyed) {
            throw new Error("Unable to emit resize event: Element Collection is destroyed.");
        }

        args.collection = this;
        this.rootCollection.windowResizeEvent.next(args);
    }
    /**
     * Allows a component to subscribe to the change notification observable to be notified when another field has changed.
     * @param callback Element's Subscriber handler. Should follow RxJS Observable pattern structure for next,error,complete
     * @returns {Subscription} The RxJS Subscription control object.
     */
    subscribeToChanges(callback) {
        if (this.isDestroyed) {
            throw new Error("Cannot subscribe to element changes: Element Collection is destroyed.");
        }

        const rootCollection = this.rootCollection;

        if (rootCollection !== this) {
            return rootCollection.subscribeToChanges(callback);
            return;
        }

        const rootStream = rootCollection.changeStream;
        return rootStream.pipe(takeUntil(this.subscriptionCleanup)).subscribe(callback);
    }

    /**
     * Subscribes a callback function to the window resize event.
     * Ensures that the subscription is valid only while the instance is not destroyed.
     *
     * @param {Function} callback - The function to be executed whenever a resize event occurs.
     * @return {Subscription} An RxJS Subscription that can be used to unsubscribe from the event.
     */
    subscribeToResize(callback) {
        if (this.isDestroyed) {
            throw new Error("Cannot subscribe to element changes: Element Collection is destroyed.");
        }

        return this.windowResizeEvent.pipe(takeUntil(this.subscriptionCleanup)).subscribe(callback);
    }
    /**
     * Build and Adds an element to the collection based on the definition
     *
     * @param definition The definition (from JSON) containing the element behaviour
     * @param formData The form object that is housing the data for the element to update
     * @param parentWrapper The DOM element in which the element should be placed
     * @returns {} Element object build and added to the collection
     */
    addElement(activator, definition, parent) {
        if (this.isDestroyed) {
            throw new Error("Cannot add elements: Element Collection is destroyed.");
        }

        if (!definition || typeof definition !== "object") {
            throw new Error("Cannot add elements: Element definition is missing or invalid.");
        }

        if (definition.elementId == null) {
            definition.elementId = definition.field ?? Tools.generateShortHash();
        }

        const element = Object.assign(new activator(parent.formData, parent, this), definition).render();

        this.addToCollection(element.fieldPath, element);
        this.addToCollection(definition.domComponent, element);

        return element;
    }

    /**
     *  @private
     *  Adds the element to the collection by Id
     * @param id Id of the element to be added. If null element is not added
     * @param element The element to add to the collection
     */
    addToCollection(id, element) {
        if (id == null) {
            return;
        }

        if (this.elements[id] !== element) {
            this.elements[id] = element;
        }
    }

    /**
     * Handles unmanaged object clean up with the collection is destroyed by the form.
     */
    destroy() {
        if (this.isDestroyed) {
            console.warn("ElementCollection is already destroyed.");
            return;
        }

        this.isDestroyed = true;

        this.subscriptionCleanup.next();
        this.subscriptionCleanup.complete();
        this.changeEvent.complete();

        this.changeEvent = null;
        this.elements = null;
    }
}
